/**
 * HumanReviewQueuePage — /review-queue (US-13-GR-07)
 *
 * Ported from the consumer portal on 2026-09-08 by David's ratified decision on
 * Trello card yvw6hcA2. Admin surfaces belong in ChellessAdmin, so the portal
 * copy at /admin/review-queue is deleted in the same change.
 *
 * Lists AITaskQueue rows parked at AwaitingHumanReview by EasyButtonService when
 * an Easy Button generation times out, lets an admin complete a task (writes the
 * human content + notifies the owner) or escalate its priority, and shows queue
 * statistics so the 24h SLA is monitorable. Non-admins get a clear "admins only"
 * state from the server 403.
 *
 * Distinct from /review-outcomes, which reads dbo.AgentReviewOutcome on
 * TaskManager.API and is untouched by this port.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowUpCircle,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import {
  getReviewQueue,
  getReviewStatistics,
  completeReviewTask,
  escalateReviewTask,
  AI_TASK_PRIORITY_VALUE,
  toAITaskPriority,
  type AITaskPriority,
  type HumanReviewTask,
  type HumanReviewStatistics,
} from '../services/humanReviewService';
import { useLatestRequest } from '../hooks/useLatestRequest';

const PRIORITY_LABELS: AITaskPriority[] = ['Critical', 'High', 'Normal', 'Low'];

const PRIORITY_BADGE: Record<AITaskPriority, string> = {
  Critical: 'bg-red-500/15 text-red-300 border-red-500/30',
  High: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  Normal: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  Low: 'bg-zinc-700/40 text-zinc-300 border-zinc-600',
};

const NEXT_PRIORITY: Record<AITaskPriority, AITaskPriority> = {
  Low: 'Normal',
  Normal: 'High',
  High: 'Critical',
  Critical: 'Critical',
};

const SLA_HOURS = 24;

function hoursSince(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}

export function HumanReviewQueuePage() {
  const [tasks, setTasks] = useState<HumanReviewTask[]>([]);
  const [stats, setStats] = useState<HumanReviewStatistics | null>(null);
  const [filter, setFilter] = useState<AITaskPriority | 'All'>('All');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  // Per-task action state
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [draftContent, setDraftContent] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const beginLoad = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginLoad();
    // NOTE: no synchronous setState before the first await — that would trip
    // react-hooks/set-state-in-effect when load() is invoked from the effect below.
    const [queueRes, statsRes] = await Promise.all([
      getReviewQueue(filter === 'All' ? undefined : filter),
      getReviewStatistics(),
    ]);
    if (!isCurrent()) return;

    if (queueRes.forbidden || statsRes.forbidden) {
      setForbidden(true);
      setIsLoading(false);
      return;
    }
    if (queueRes.success && queueRes.data) {
      setTasks(queueRes.data);
      setError(null);
    } else {
      setError(queueRes.error ?? 'Could not load the review queue.');
    }
    if (statsRes.success && statsRes.data) {
      setStats(statsRes.data);
    }
    setIsLoading(false);
  }, [beginLoad, filter]);

  useEffect(() => {
    // Intentional data-fetch on mount / filter change. load() updates state only after
    // awaiting the network; the cascading-render case react-hooks/set-state-in-effect
    // guards against does not apply here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const handleComplete = async (task: HumanReviewTask) => {
    const content = (draftContent[task.id] ?? '').trim();
    if (!content) {
      setActionError('Enter the completed content before marking the task done.');
      return;
    }
    setBusyId(task.id);
    setActionError(null);
    const res = await completeReviewTask(task.id, content);
    setBusyId(null);
    if (res.success) {
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      setExpandedId(null);
    } else {
      setActionError(res.error ?? 'Could not complete the task.');
    }
  };

  const handleEscalate = async (task: HumanReviewTask, newPriority: AITaskPriority) => {
    setBusyId(task.id);
    setActionError(null);
    const res = await escalateReviewTask(task.id, newPriority);
    setBusyId(null);
    if (res.success && res.data) {
      // Keep the sent name if the response omits priority; toAITaskPriority handles name or number.
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...res.data!, priority: toAITaskPriority(res.data!.priority ?? newPriority) } : t))
      );
    } else {
      setActionError(res.error ?? 'Could not escalate the task.');
    }
  };

  const sortedTasks = useMemo(
    () =>
      [...tasks].sort((a, b) => {
        const rankA = AI_TASK_PRIORITY_VALUE[toAITaskPriority(a.priority)];
        const rankB = AI_TASK_PRIORITY_VALUE[toAITaskPriority(b.priority)];
        if (rankB !== rankA) return rankB - rankA;
        return new Date(a.queuedAt).getTime() - new Date(b.queuedAt).getTime();
      }),
    [tasks]
  );

  if (forbidden) {
    return (
      <main className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-6">
        <div className="mx-auto mt-10 w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-8 text-center">
          <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-amber-400" />
          <h1 className="text-lg font-semibold text-zinc-100">Admins only</h1>
          <p className="mt-2 text-sm text-zinc-400">
            The human-review queue is restricted to platform administrators.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-sm font-semibold text-zinc-100">Human Review Queue</h1>
          <p className="text-xs text-zinc-500">Easy Button tasks awaiting a person</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          aria-label="Refresh queue"
          className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-800 px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-700 disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Awaiting" value={stats.totalAwaitingReview} />
          <StatCard label="Overdue (24h)" value={stats.overdueTasks} danger={stats.overdueTasks > 0} />
          <StatCard label="Avg wait" value={`${stats.averageWaitTimeHours.toFixed(1)}h`} />
          <StatCard label="Completion" value={`${Math.round(stats.completionRate * 100)}%`} />
        </div>
      )}

      {/* Priority filter */}
      <div className="mb-4 flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter by priority">
        {(['All', ...PRIORITY_LABELS] as const).map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={filter === p}
            onClick={() => setFilter(p)}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
              filter === p
                ? 'border-violet-500 bg-violet-500/15 text-violet-200'
                : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600'
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {isLoading && tasks.length === 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900 p-4 text-sm text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin text-violet-400" /> Loading queue…
        </div>
      )}

      {!isLoading && tasks.length === 0 && !error && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-8 text-center">
          <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-400" />
          <p className="text-sm text-zinc-300">The review queue is empty. Nothing needs a person right now.</p>
        </div>
      )}

      <ul className="space-y-3">
        {sortedTasks.map((task) => {
          const label = toAITaskPriority(task.priority);
          const waited = hoursSince(task.queuedAt);
          const overdue = waited >= SLA_HOURS;
          const isOpen = expandedId === task.id;
          const isBusy = busyId === task.id;

          return (
            <li key={task.id} className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${PRIORITY_BADGE[label]}`}>
                      {label}
                    </span>
                    {overdue && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[11px] font-medium text-red-300">
                        <Clock className="h-3 w-3" /> Overdue
                      </span>
                    )}
                  </div>
                  <h3 className="mt-1.5 truncate text-sm font-semibold text-zinc-100">{task.taskTitle}</h3>
                  <p className="mt-0.5 line-clamp-2 text-xs text-zinc-400">{task.taskDescription}</p>
                  <p className="mt-1 text-[11px] text-zinc-500">
                    Queued {waited < 1 ? '<1' : Math.floor(waited)}h ago
                    {task.humanReviewReason ? ` · ${task.humanReviewReason}` : ''}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setExpandedId(isOpen ? null : task.id)}
                  className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-zinc-950 hover:bg-violet-500"
                >
                  {isOpen ? 'Cancel' : 'Complete task'}
                </button>

                {/* Escalate to the next-higher priority (hidden when already Critical). */}
                {label !== 'Critical' && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => handleEscalate(task, NEXT_PRIORITY[label])}
                    className="inline-flex items-center gap-1 rounded-lg border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-600 disabled:opacity-50"
                  >
                    <ArrowUpCircle className="h-3.5 w-3.5" /> Escalate
                  </button>
                )}
              </div>

              {isOpen && (
                <div className="mt-3 border-t border-zinc-800 pt-3">
                  <label className="mb-1 block text-xs text-zinc-400" htmlFor={`content-${task.id}`}>
                    Completed content
                  </label>
                  <textarea
                    id={`content-${task.id}`}
                    value={draftContent[task.id] ?? ''}
                    onChange={(e) =>
                      setDraftContent((prev) => ({ ...prev, [task.id]: e.target.value }))
                    }
                    rows={5}
                    placeholder="Write the content that satisfies this task. The owner will be notified."
                    className="w-full resize-y rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-violet-500 focus:outline-none"
                  />
                  {actionError && (
                    <p className="mt-1 text-xs text-red-400" role="status">{actionError}</p>
                  )}
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => handleComplete(task)}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                  >
                    {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Mark complete &amp; notify owner
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </main>
  );
}

function StatCard({ label, value, danger }: { label: string; value: string | number; danger?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${danger ? 'border-red-500/30 bg-red-500/10' : 'border-zinc-800 bg-zinc-900'}`}>
      <p className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${danger ? 'text-red-300' : 'text-zinc-100'}`}>{value}</p>
    </div>
  );
}

export default HumanReviewQueuePage;
